/**
 * Three cards above the policy library that answer a reviewer's first three
 * questions: is anything overdue, how much of the library is actually still
 * good (the freshness chart), and does everything have an owner.
 *
 * Every figure comes from governance rows that exist; a document nobody has
 * governed is counted honestly as ungoverned rather than defaulted into a
 * status it never had.
 *
 * @module components/regulation/PolicyGovernanceCards
 */
import { DONUT_SEGMENT_PROPS, DONUT_TOOLTIP_PROPS, GovernancePieSector } from "./GovernancePieSector";
import { CalendarClockIcon, ChartPieIcon, UserCheckIcon } from "lucide-react";
import { useMemo, type ComponentType } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { cn } from "~/lib/utils";
import type { Policy } from "../../state/policies";
import type { OrgPolicyGovernance } from "../../state/governance";
import { ChartTooltipContent } from "../ui/chart";
import { Bone } from "./LoadingSkeletons";
import { freshnessSlices } from "./policy/policyFreshness";

const DAY_MS = 24 * 60 * 60 * 1000;

export interface ReviewHealth {
  overdue: number;
  dueSoon: number;
  onTrack: number;
  noCycle: number;
}

/** Review posture across governed policies. Due soon = within 30 days. */
export function reviewHealth(
  rows: ReadonlyArray<Pick<OrgPolicyGovernance, "reviewDueAt" | "reviewFrequency">>,
  now: number,
): ReviewHealth {
  const health: ReviewHealth = { overdue: 0, dueSoon: 0, onTrack: 0, noCycle: 0 };
  for (const row of rows) {
    if (row.reviewDueAt === null) {
      if (row.reviewFrequency === "none") health.noCycle += 1;
      else health.onTrack += 1;
      continue;
    }
    const due = Date.parse(row.reviewDueAt);
    if (Number.isNaN(due)) {
      health.noCycle += 1;
    } else if (due < now) {
      health.overdue += 1;
    } else if (due < now + 30 * DAY_MS) {
      health.dueSoon += 1;
    } else {
      health.onTrack += 1;
    }
  }
  return health;
}

function Card({
  icon: Icon,
  label,
  children,
}: {
  readonly icon: ComponentType<{ className?: string }>;
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-border/60 bg-card p-4">
      <span className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
        <Icon className="size-4" />
        {label}
      </span>
      {children}
    </div>
  );
}

function CountRow({
  label,
  value,
  tone = "muted",
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: "destructive" | "warning" | "primary" | "muted";
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="flex items-center gap-2 text-foreground">
        <span
          aria-hidden
          className={cn(
            "size-2 rounded-full",
            tone === "destructive" && "bg-destructive",
            tone === "warning" && "bg-primary/50",
            tone === "primary" && "bg-primary",
            tone === "muted" && "bg-muted-foreground/40",
          )}
        />
        {label}
      </span>
      <span className="font-medium tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * How much of the library is still good: a donut of freshness with the
 * up-to-date count in the middle, and every slice named and counted beside
 * it so the answer never rides on colour alone.
 */
function FreshnessCard({
  policies,
  governance,
  touchedPolicyIds,
  openFindings,
  now,
}: {
  readonly policies: ReadonlyArray<Policy>;
  readonly governance: ReadonlyMap<string, OrgPolicyGovernance>;
  readonly touchedPolicyIds: ReadonlySet<string>;
  readonly openFindings: ReadonlyMap<string, number> | undefined;
  readonly now: number;
}) {
  const slices = useMemo(
    () => freshnessSlices(policies, governance, touchedPolicyIds, now, openFindings),
    [policies, governance, touchedPolicyIds, now, openFindings],
  );
  const current = slices.find((slice) => slice.key === "current")?.count ?? 0;

  return (
    <Card icon={ChartPieIcon} label="Freshness">
      <div className="flex items-center gap-4">
        <div className="relative size-28 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                  shape={GovernancePieSector}
                data={[...slices]}
                dataKey="count"
                nameKey="label"
                innerRadius="72%"
                outerRadius="100%"
                cornerRadius={4}
                {...DONUT_SEGMENT_PROPS}
              >
                {slices.map((slice) => (
                  <Cell key={slice.key} fill={slice.color} />
                ))}
              </Pie>
              <Tooltip {...DONUT_TOOLTIP_PROPS} content={<ChartTooltipContent hideLabel />} />
            </PieChart>
          </ResponsiveContainer>
          <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
              {current}
            </span>
            <span className="text-[11px] leading-tight text-muted-foreground">
              of {policies.length}
            </span>
          </span>
        </div>
        <ul className="min-w-0 flex-1 space-y-1">
          {slices.map((slice) => (
            <li key={slice.key} className="flex items-center gap-2 text-sm">
              <span
                aria-hidden
                className="h-3.5 w-1 shrink-0 rounded-full"
                style={{ background: slice.color }}
              />
              <span className="min-w-0 flex-1 truncate text-foreground">{slice.label}</span>
              <span className="font-medium tabular-nums text-foreground">{slice.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

export function PolicyGovernanceCards({
  policies,
  governance,
  touchedPolicyIds,
  openFindings,
  isLoading = false,
  unavailable = false,
  className,
  setupAction,
  now = Date.now(),
}: {
  readonly policies: ReadonlyArray<Policy>;
  readonly governance: ReadonlyMap<string, OrgPolicyGovernance>;
  /** Policies a regulatory update names — they count as out of date. */
  readonly touchedPolicyIds: ReadonlySet<string>;
  /** Open findings per document — a document with one is not up to date. */
  readonly openFindings?: ReadonlyMap<string, number>;
  /**
   * True while the governance rows are still being fetched for the first
   * time. An empty map then means "don't know yet", not "not set up" — the
   * cards hold their space with bones instead of flashing the setup pitch
   * over a library that may be fully governed.
   */
  readonly isLoading?: boolean;
  /** True when the governance endpoint answered "unavailable" — say nothing. */
  readonly unavailable?: boolean;
  readonly className?: string;
  /** Rendered on the right of the "not set up yet" card — the way in. */
  readonly setupAction?: React.ReactNode;
  readonly now?: number;
}) {
  const governed = policies
    .map((policy) => governance.get(policy.id))
    .filter((row): row is OrgPolicyGovernance => row !== undefined);
  const ungoverned = policies.length - governed.length;

  // The endpoint said governance doesn't exist here; a setup pitch would be
  // a lie about a server-side condition. The table below still stands.
  if (unavailable) {
    return null;
  }

  // Still waiting on the first governance answer: hold the cards' space so
  // the page doesn't claim "not set up" and then snap into three cards.
  if (isLoading && governed.length === 0) {
    return (
      <div
        role="status"
        aria-label="Loading governance"
        className={cn("grid gap-3 sm:grid-cols-3", className)}
      >
        {Array.from({ length: 3 }, (_, index) => (
          <div
            key={index}
            className="flex min-w-0 flex-col gap-3 rounded-xl border border-border/60 bg-card p-4"
          >
            <Bone className="h-4 w-28" />
            <Bone className="h-3.5 w-3/4" />
            <Bone className="h-3.5 w-1/2" />
            <Bone className="h-3.5 w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  const health = reviewHealth(governed, now);
  const assigned = governed.filter((row) => row.ownerId !== null).length;
  const unassigned = policies.length - assigned;

  // Three cards of zeros read as a broken dashboard, not an honest one. Until
  // at least one policy is governed there is exactly one fact worth showing:
  // governance has not been set up.
  if (governed.length === 0 && policies.length > 0) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-4 rounded-xl border border-border/60 bg-card p-4",
          className,
        )}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <UserCheckIcon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">Governance isn't set up yet</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {policies.length === 1
              ? "Your document has no owner or review cycle."
              : `None of these ${policies.length} documents has an owner or review cycle.`}{" "}
            Open a policy and use its Settings tab to assign an owner, set a review
            cadence, and track it through publication.
          </p>
        </div>
        {setupAction}
      </div>
    );
  }

  return (
    <div className={cn("grid gap-3 sm:grid-cols-3", className)}>
      <Card icon={CalendarClockIcon} label="Review health">
        <div className="space-y-1.5">
          <CountRow label="Overdue" value={health.overdue} tone="destructive" />
          <CountRow label="Due in 30 days" value={health.dueSoon} tone="warning" />
          <CountRow label="On track" value={health.onTrack} tone="primary" />
          <CountRow label="No review cycle" value={health.noCycle + ungoverned} />
        </div>
      </Card>

      <FreshnessCard
        policies={policies}
        governance={governance}
        touchedPolicyIds={touchedPolicyIds}
        openFindings={openFindings}
        now={now}
      />

      <Card icon={UserCheckIcon} label="Ownership">
        <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
          {assigned}
          <span className="text-sm font-normal text-muted-foreground">
            {" "}
            of {policies.length} assigned
          </span>
        </p>
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={
            policies.length === 0
              ? "Nothing to assign yet"
              : `${assigned} of ${policies.length} policies have an owner`
          }
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
            style={{
              width: `${policies.length === 0 ? 0 : Math.round((assigned / policies.length) * 100)}%`,
            }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {unassigned === 0 && policies.length > 0
            ? "Every policy has someone accountable."
            : `${unassigned} ${unassigned === 1 ? "policy needs" : "policies need"} an owner.`}
        </p>
      </Card>
    </div>
  );
}
